import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { GoTriangleDown } from "react-icons/go";

const UserMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();
  const { user, isAuth, logout } = useAuth();
  const location = useLocation();

  const isUserAuth = Boolean(user?.fullName && isAuth);

  const toggleDropdown = useCallback(() => {
    setIsOpen((v) => !v);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const logOutHandler = useCallback(async () => {
    try {
      await logout();
      closeDropdown();
    } catch (error) {
      console.error(error);
    }
  }, [logout, closeDropdown]);

  // Close when clicking/touching outside
  useEffect(() => {
    function handleOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("click", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("click", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close when route changes (ensures mobile nav closes after navigation)
  useEffect(() => {
    // location changes whenever navigation happens
    setIsOpen(false);
  }, [location.pathname]);

  if (!isUserAuth) {
    return (
      <Link
        to="/login"
        className="text-white border border-white hover:bg-white hover:text-black rounded-md px-3 py-2 text-sm font-medium"
      >
        {t("login")}
      </Link>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="relative text-center flex justify-center items-center"
    >
      <button
        onClick={toggleDropdown}
        type="button"
        className="inline-flex items-center text-center text-gray-900 rounded-lg hover:shadow-lg dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
        id="dropdownMenuIconButton"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <img
          //   src={`${ASSET_URI}/${user?.image}`}
          //   src={user?.image ? `${ASSET_URI}/${user.image}` : `${ASSET_URI}/default-profile.png`}
          src={`/default-avatar.jpg`}
          title={user?.fullName}
          className="w-8 h-8 lg:w-9 lg:h-9 rounded-full object-cover"
          width={32}
          height={32}
          alt={user?.fullName}
          loading="lazy"
          crossOrigin="anonymous"
        />
        <GoTriangleDown color="black" />
      </button>

      {isOpen && (
        <div
          className="z-10 absolute top-2 lg:top-5 ltr:right-0 rtl:left-0 mt-5 w-32 bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-gray-700 dark:divide-gray-600"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="dropdownMenuIconButton"
        >
          <ul className="py-1.5 text-sm dark:text-gray-200">
            <li>
              <Link
                to="/dashboard"
                onClick={closeDropdown}
                className="block text-center w-full text-black text-md lg:text-md font-bold cursor-pointer px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
              >
                {t("dashboard")}
              </Link>
            </li>
            {user?.role === "teacher" && user?.isAdmin !== true && user?.studioAccess ? (
              <li>
                <Link
                  to="/teacher/courses"
                  onClick={closeDropdown}
                  className="block text-center w-full text-black text-md lg:text-md font-bold cursor-pointer px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  {t("teacherStudio.title", "Teacher Studio")}
                </Link>
              </li>
            ) : null}
            <li>
              <Link
                to="/profile"
                onClick={closeDropdown}
                className="block text-center w-full text-black text-md lg:text-md font-bold cursor-pointer px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
              >
                {t("profile")}
              </Link>
            </li>
          </ul>

          <div className="py-2">
            <button
              className="block w-full text-black text-md font-bold cursor-pointer px-2 py-1 text-md lg:text-md hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white"
              onClick={logOutHandler}
            >
              {t("logout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
